// Thin wrappers around the CSV endpoints. apiRequest doesn't fit either case:
//   - Export returns text/csv as a stream — we want a Blob, not JSON.
//   - Import sends multipart/form-data — apiRequest stringifies bodies.
// So we hit fetch directly and reuse the same Bearer/dev-header injection.

import { accessToken, refreshIfNeeded } from "./auth";

const DEV_USER_ID = import.meta.env.VITE_DEV_USER_ID ?? "";

export type CsvEntityPlural = "accounts" | "contacts" | "deals" | "products" | "activities";

export type CsvImportOutcome = "created" | "updated" | "error";

export type CsvImportRowResult = {
  line_no: number;
  ok: boolean;
  outcome: CsvImportOutcome;
  entity_id: string | null;
  error: string | null;
  error_details: Record<string, unknown> | null;
};

export type CsvImportResponse = {
  entity_type: "account" | "contact" | "deal" | "product" | "activity";
  total: number;
  created: number;
  updated: number;
  rejected: number;
  dry_run: boolean;
  upsert_key: string | null;
  rows: CsvImportRowResult[];
};

async function authHeaders(): Promise<Record<string, string>> {
  const tok = await refreshIfNeeded().catch(() => accessToken());
  const bearer = tok ?? accessToken();
  if (bearer) return { Authorization: `Bearer ${bearer}` };
  if (DEV_USER_ID) return { "X-Dev-User-Id": DEV_USER_ID };
  return {};
}

export async function downloadCsv(
  wsKey: string,
  plural: CsvEntityPlural,
  options?: { includeArchived?: boolean },
): Promise<void> {
  const params = new URLSearchParams();
  if (options?.includeArchived) params.set("include_archived", "true");
  const qs = params.toString();
  const res = await fetch(`/v1/workspaces/${wsKey}/${plural}:export${qs ? `?${qs}` : ""}`, {
    headers: await authHeaders(),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const filename =
    filenameFromDisposition(res.headers.get("content-disposition")) ?? `scaicrm-${plural}.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function uploadCsv(
  wsKey: string,
  plural: CsvEntityPlural,
  file: File,
  { dryRun, upsertKey }: { dryRun: boolean; upsertKey?: string | null },
): Promise<CsvImportResponse> {
  const form = new FormData();
  form.append("file", file);
  const params = new URLSearchParams({ dry_run: dryRun ? "true" : "false" });
  if (upsertKey) params.set("upsert_key", upsertKey);
  const res = await fetch(`/v1/workspaces/${wsKey}/${plural}:import?${params.toString()}`, {
    method: "POST",
    headers: await authHeaders(),
    body: form,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Import failed: ${res.status} ${text}`);
  }
  return (await res.json()) as CsvImportResponse;
}

function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = header.match(/filename="([^"]+)"/);
  return match ? match[1] : null;
}

// ── Custom records ───────────────────────────────────────────────────────────

export type CustomRecordImportRowResult = {
  line_no: number;
  ok: boolean;
  outcome: "created" | "error";
  record_id: string | null;
  error: string | null;
};

export type CustomRecordImportResponse = {
  total: number;
  created: number;
  rejected: number;
  dry_run: boolean;
  rows: CustomRecordImportRowResult[];
};

export async function downloadCustomRecordsCsv(
  wsKey: string,
  codId: string,
  objectKey: string,
  options?: { includeArchived?: boolean },
): Promise<void> {
  const params = new URLSearchParams();
  if (options?.includeArchived) params.set("include_archived", "true");
  const qs = params.toString();
  const res = await fetch(
    `/v1/workspaces/${wsKey}/custom_objects/${codId}/records:export${qs ? `?${qs}` : ""}`,
    { headers: await authHeaders() },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const filename =
    filenameFromDisposition(res.headers.get("content-disposition")) ?? `scaicrm-${objectKey}.csv`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function uploadCustomRecordsCsv(
  wsKey: string,
  codId: string,
  file: File,
  { dryRun }: { dryRun: boolean },
): Promise<CustomRecordImportResponse> {
  const form = new FormData();
  form.append("file", file);
  const params = new URLSearchParams({ dry_run: dryRun ? "true" : "false" });
  const res = await fetch(
    `/v1/workspaces/${wsKey}/custom_objects/${codId}/records:import?${params.toString()}`,
    { method: "POST", headers: await authHeaders(), body: form },
  );
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Import failed: ${res.status} ${text}`);
  }
  return (await res.json()) as CustomRecordImportResponse;
}
